/**
 * jk-product-price.js
 *
 * Aktualisiert den PDP-Preis-Block bei:
 *   - Mengen-Kachel-Wechsel (jk-quantity-breaks)
 *   - Abo-Auswahl/-Abwahl (Selling-Plan-Inputs im Produktformular)
 *   - Variantenwechsel (Dawn PUB_SUB_EVENTS.variantChange)
 *
 * Verhaltensregeln (Entscheid Andreas 28.07.2026):
 *   - Fail-safe: Ein rabattierter Abo-Preis wird NUR gezeigt, wenn die
 *     gelesene Selling-Plan-ID eindeutig auf eine Allocation aus dem
 *     serverseitig gerenderten JSON matcht. Sonst bleibt der
 *     Einmalkaufpreis stehen. Kein hartkodierter Rabatt-Faktor.
 *   - Keine Abhaengigkeit von Seal-Wurzelklassen: gelesen wird
 *     input[name="selling_plan"] im Produktformular (Event-Delegation),
 *     MutationObserver auf dem Formular nur als Fallback fuer
 *     programmatische Re-Renders ohne change-Event.
 *   - DOM-Referenzen werden pro Update frisch aufgeloest: Dawn ersetzt
 *     bei Variantenwechsel den Inhalt von #price-<sectionId> per
 *     innerHTML (product-info.js), gecachte Knoten waeren detached.
 *
 * Fasst NICHT an: ATC-Logik, selling_plan-Formularwerte, Cart, Galerie.
 */
(function () {
  'use strict';

  if (!document.body.classList.contains('template-product')) return;
  if (!document.querySelector('.jk-price-block')) return;

  // ── Helpers ───────────────────────────────────────────────────

  // Format cents → money string. Deckt ALLE Shopify-Money-Platzhalter ab
  // (EUR-Maerkte liefern {{amount_with_comma_separator}} — vorher blieb
  // der Platzhalter roh stehen, Befund Andreas 15.08.2026). Laengste
  // Platzhalter zuerst; Tausender entfallen bewusst (Preise < 10'000).
  function fmt(cents, moneyFormat) {
    const amount = (Math.round(cents) / 100).toFixed(2);
    const comma  = amount.replace('.', ',');
    const noDec  = String(Math.round(cents / 100));
    return moneyFormat
      .replace('{{amount_no_decimals_with_comma_separator}}', noDec)
      .replace('{{amount_no_decimals_with_space_separator}}', noDec)
      .replace('{{amount_with_comma_separator}}',             comma)
      .replace('{{amount_with_apostrophe_separator}}',        amount)
      .replace('{{amount_with_period_and_space_separator}}',  amount)
      .replace('{{amount_no_decimals}}',                      noDec)
      .replace('{{amount_with_currency}}',                    amount)
      .replace('{{amount}}',                                  amount);
  }

  // Read current variant from Dawn's [data-selected-variant] blob (multi-variant only).
  function getVariant() {
    const blob = document.querySelector('[data-selected-variant]');
    if (!blob) return null;
    try { return JSON.parse(blob.textContent); } catch { return null; }
  }

  // v03 (03.09.2026): Varianten-Preisdaten aus dem serverseitigen JSON
  // (jk-main-product rendert unit_price/unit_ref/apps pro variant.id —
  // Quelle fuer Grundpreis und Preis/Anwendung; das Script liegt
  // ausserhalb des innerHTML-Swap-Containers und bleibt daher stabil).
  function getVariantPriceData(variantId) {
    if (!variantId) return null;
    const script = document.querySelector('[data-jk-variant-price-data]');
    if (!script) return null;
    try {
      const map = JSON.parse(script.textContent);
      return map[String(variantId)] || null;
    } catch { return null; }
  }

  // Read currently checked qty-breaks radio.
  function getQtySelection() {
    const qb    = document.querySelector('jk-quantity-breaks');
    const radio = qb ? qb.querySelector('input[type="radio"]:checked') : null;
    return {
      qty:      parseInt(radio?.dataset.qty      || '1', 10) || 1,
      discount: parseInt(radio?.dataset.discount || '0', 10) || 0
    };
  }

  // Normalize plan ID — handles both numeric (123456) and GID formats
  // (gid://shopify/SellingPlan/123456 → 123456)
  function normalizePlanId(id) {
    return String(id || '').split('/').pop().trim();
  }

  // Read the active selling plan — name-/event-basiert, keine App-Klassen.
  // Verifiziert gegen Seals Widget-Quelltext (seal-ac-module.js, 28.07.2026):
  // Seals Radios heissen "subs_type_<key>" (Value = Gruppen-INDEX, nicht
  // Plan-ID); die echte Plan-ID pflegt Seal in einem Hidden-Input
  // input[type=hidden][name="selling_plan"][data-sls-selling_plan] — im
  // Widget-Container und/oder (per prepend) im Produktformular. Zusaetzlich
  // feuert Seal "sealsubs:selling_plan_changed" mit detail.selling_plan_id.
  // Lese-Prioritaet (DOM zuerst — der Hidden-Input im Formular ist, was der
  // ATC-POST tatsaechlich sendet):
  //   1. checked Radio name="selling_plan" (Standard-Shopify-Pattern, nicht
  //      Seal — Zukunftssicherheit)
  //   2. Hidden-Inputs name="selling_plan": Formular zuerst, dann dokumentweit
  //      (Widget-Container kann ausserhalb des <form> liegen)
  //   3. Zuletzt per Seal-Event gemeldete Plan-ID
  // Leerstring = Einmalkauf.
  let lastAnnouncedPlanId = null; // null = noch kein Seal-Event empfangen

  function getSellingPlanId() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const checked = (form || document).querySelector('input[name="selling_plan"]:checked');
    if (checked) return checked.value || '';

    const hiddenSelector = 'input[name="selling_plan"]:not([type="radio"]):not([type="checkbox"])';
    const hiddens = [];
    if (form) hiddens.push.apply(hiddens, form.querySelectorAll(hiddenSelector));
    hiddens.push.apply(hiddens, document.querySelectorAll(hiddenSelector));
    for (const el of hiddens) {
      if (el.value) return el.value;
    }
    if (hiddens.length > 0) return ''; // Hidden-Inputs vorhanden, alle leer = Einmalkauf

    return lastAnnouncedPlanId || '';
  }

  // ── Main update ───────────────────────────────────────────────

  function update() {
    const priceBlock = document.querySelector('.jk-price-block');
    if (!priceBlock) return;

    // Frische Referenzen — siehe Header (innerHTML-Swap bei Variantenwechsel)
    const priceEl   = priceBlock.querySelector('.jk-price-block__price');
    const compareEl = priceBlock.querySelector('.jk-price-block__compare');
    const unitEl    = priceBlock.querySelector('.jk-price-block__unit');
    // Paket C (03.09.2026): innerer [data-jk-per-app]-Span bevorzugt —
    // die statische Anwendungszahl daneben (Einzelvariante) bleibt so
    // beim Update erhalten; Klassen-Fallback fuer alte Swap-Inhalte.
    const appEl     = priceBlock.querySelector('[data-jk-per-app]') || priceBlock.querySelector('.jk-price-block__application');

    const moneyFormat = priceBlock.dataset.moneyFormat   || 'CHF {{amount}}';
    // Lokalisiertes Template aus dem Liquid ('jk.product.per_application'
    // mit __AMT__-Sentinel) — i18n-Regel: JS erhaelt Texte nur via data-*.
    const perAppTpl   = priceBlock.dataset.perAppTemplate || '__AMT__/application';

    const variant   = getVariant();

    // v03: Werte je Variante aus dem serverseitigen JSON; die data-*
    // des Containers sind nur noch der Fallback fuer Ein-Varianten-
    // Produkte (kein Varianten-Blob, kein Datenscript im Markup).
    const vd = getVariantPriceData(variant && variant.id);
    const appsCount      = vd ? (parseInt(vd.apps, 10) || 0)       : parseInt(priceBlock.dataset.appsCount || '0', 10);
    const unitPriceCents = vd ? (parseInt(vd.unit_price, 10) || 0) : parseInt(priceBlock.dataset.unitPrice || '0', 10);
    const unitRef        = vd ? (parseFloat(vd.unit_ref) || 0)     : parseFloat(priceBlock.dataset.unitRef || '0');
    const unitRefUnit    = vd ? (vd.unit_ref_unit || '')           : (priceBlock.dataset.unitRefUnit || '');
    // Base price: prefer live variant JSON (multi-variant), fall back to Liquid data attr
    const baseCents = variant?.price || parseInt(priceBlock.dataset.basePrice || '0', 10);
    if (!baseCents) return;

    // Allocations: live variant JSON zuerst, initiales Liquid-JSON als Fallback.
    // Beide Quellen sind serverseitig gerendert ({{ selling_plan_allocations | json }}).
    let allocations = variant?.selling_plan_allocations;
    if (!allocations || allocations.length === 0) {
      const sectionId = priceBlock.id.replace('price-', '');
      const spScript  = document.getElementById('jk-selling-plans-' + sectionId);
      if (spScript) {
        try { allocations = JSON.parse(spScript.textContent) || []; } catch { allocations = []; }
      }
    }
    if (!allocations) allocations = [];

    const { qty, discount } = getQtySelection();
    const spId = getSellingPlanId();

    // Fail-safe: Abo-Preis NUR bei verifiziertem Allocation-Match.
    let unitCents      = baseCents;
    let isSubscription = false;
    if (spId) {
      const normalizedSpId = normalizePlanId(spId);
      const alloc = allocations.find(function (a) {
        const planId = a.selling_plan_id ?? a.selling_plan?.id;
        return normalizePlanId(planId) === normalizedSpId;
      });
      if (alloc?.price) {
        unitCents      = alloc.price;
        isSubscription = true;
      }
      // Kein Match → Einmalkaufpreis bleibt stehen (kein Fallback-Rabatt).
    }

    // Calculate total and compare
    let totalCents, compareCents;
    if (isSubscription) {
      // Abo ist auf Menge 1 beschraenkt (jk-subscription-visibility.js)
      totalCents   = unitCents;
      compareCents = baseCents > unitCents ? baseCents : null;
    } else {
      // Bundle: base × qty × (1 - discount%)
      totalCents   = Math.round(baseCents * qty * (100 - discount) / 100);
      compareCents = discount > 0 ? baseCents * qty : null;
    }

    // ── Update DOM ─────────────────────────────────────────────

    if (priceEl) priceEl.textContent = fmt(totalCents, moneyFormat);

    // Inline-Abo-Referenzpreis neben dem Hauptpreis; bei Variantenwechsel
    // wird der injizierte Span mit weggewischt → bei Bedarf neu anlegen.
    let subCompareEl = priceBlock.querySelector('.jk-subscription-compare-price');
    if (isSubscription) {
      priceBlock.classList.add('jk-price-block--subscription');
      if (!subCompareEl) {
        subCompareEl = document.createElement('span');
        subCompareEl.className = 'jk-subscription-compare-price';
        const mainDiv = priceBlock.querySelector('.jk-price-block__main');
        if (mainDiv) mainDiv.appendChild(subCompareEl);
      }
      subCompareEl.textContent = fmt(baseCents, moneyFormat);
      subCompareEl.hidden      = false;
      if (compareEl) compareEl.hidden = true;
    } else {
      priceBlock.classList.remove('jk-price-block--subscription');
      if (subCompareEl) subCompareEl.hidden = true;
      if (compareEl) {
        if (compareCents && compareCents > totalCents) {
          compareEl.textContent = fmt(compareCents, moneyFormat);
          compareEl.hidden      = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    // Spar-Badge Zeile 1 (Entscheid Andreas 18.08.2026): mit dem Wegfall
    // der Kachel- und Widget-Preise ist der Badge die einzige Stelle mit
    // dem absoluten Sparbetrag in CHF — er deckt Mengen-Rabatt,
    // Varianten-Sale UND Abo-Rabatt ab (Seal-Briefing 18.08.2026:
    // Preisblock inkl. Badge muss die Abo-Auswahl live abbilden).
    // Voller Wert = max(compare_at, Basispreis) x Menge (Abo: Menge 1).
    // Text kommt komplett aus dem lokalisierten data-save-badge-template.
    const saveTpl = priceBlock.dataset.saveBadgeTemplate || '';
    let saveBadgeEl = priceBlock.querySelector('.jk-badge--savings');
    if (saveTpl) {
      const compareBase = variant?.compare_at_price || parseInt(priceBlock.dataset.comparePrice || '0', 10) || 0;
      const unitFull   = compareBase > baseCents ? compareBase : baseCents;
      const fullCents  = unitFull * (isSubscription ? 1 : qty);
      const saveCents  = fullCents - totalCents;
      if (fullCents > 0 && saveCents > 0) {
        const pct = Math.round(saveCents * 100 / fullCents);
        if (!saveBadgeEl) {
          const mainDiv = priceBlock.querySelector('.jk-price-block__main');
          if (mainDiv) {
            saveBadgeEl = document.createElement('span');
            saveBadgeEl.className = 'jk-badge jk-badge--savings';
            mainDiv.appendChild(saveBadgeEl);
          }
        }
        if (saveBadgeEl) {
          saveBadgeEl.textContent = saveTpl.replace('__PCT__', String(pct)).replace('__AMT__', fmt(saveCents, moneyFormat));
          saveBadgeEl.hidden = false;
        }
      } else if (saveBadgeEl) {
        saveBadgeEl.hidden = true;
      }
    }

    // v03 (03.09.2026): KEINE Volumen-Arithmetik mehr — Quelle ist
    // Shopifys variant.unit_price (serverseitig im Varianten-JSON).
    // Abo-/Mengenrabatt skaliert ueber das Preisverhaeltnis:
    // perRef = unit_price x (effektiver Stueckpreis / Basispreis);
    // ohne Rabatt ist das Verhaeltnis 1 -> exakt Shopifys Wert.
    if (unitEl && unitPriceCents > 0 && unitRefUnit && baseCents > 0) {
      const effectiveQty = isSubscription ? 1 : qty;
      const perItemCents = totalCents / effectiveQty;
      const perRef       = Math.round(unitPriceCents * perItemCents / baseCents);
      const refDisplay   = unitRef !== 1 ? String(Math.round(unitRef)) + '\u00a0' : '';
      unitEl.textContent = fmt(perRef, moneyFormat) + ' / ' + refDisplay + unitRefUnit;
    }

    // Price per application (Entscheid Andreas 18.08.2026: NUR der
    // Preis/Anwendung — die Anwendungszahl ist aus dem Preisblock
    // gestrichen, sie steht in den Bullets). Text kommt komplett aus
    // dem lokalisierten data-per-app-template (__AMT__-Sentinel).
    if (appEl && appsCount > 0) {
      const effectiveQty = isSubscription ? 1 : qty;
      const totalApps    = appsCount * effectiveQty;
      const perApp       = Math.round(totalCents / totalApps);
      appEl.textContent  = perAppTpl.replace('__AMT__', fmt(perApp, moneyFormat));
    }

    // Paket H1 (03.09.2026, ersetzt das Listen-Toggling aus G): nur die
    // pro Variante vorgehaltenen ZEILEN (Ergiebigkeitszeile, kuenftige
    // Bullet-Overrides) werden umgeschaltet — hidden + aria-hidden,
    // kein Text und keine Arithmetik im JS.
    if (variant && variant.id) {
      document.querySelectorAll('[data-jk-yield-variant], [data-jk-bullet-variant]').forEach(function (item) {
        var vid = item.getAttribute('data-jk-yield-variant') || item.getAttribute('data-jk-bullet-variant');
        var off = String(variant.id) !== vid;
        item.hidden = off;
        if (off) {
          item.setAttribute('aria-hidden', 'true');
        } else {
          item.removeAttribute('aria-hidden');
        }
      });
    }

    // Spec-Sheet-Sync ([data-jk-spec-reach]/[data-jk-spec-size]) entfernt
    // (v03, 03.09.2026): die Elemente existieren in keinem Liquid mehr —
    // toter Code inkl. hartkodiertem deutschem String (i18n-Verstoss).
    // Sticky-ATC-Sync entfernt (Rueckbau 19.08.2026).
  }

  // ── Listeners ─────────────────────────────────────────────────

  let updatePending = false;
  function scheduleUpdate() {
    if (updatePending) return;
    updatePending = true;
    requestAnimationFrame(function () {
      updatePending = false;
      update();
    });
  }

  // 1. Event-Delegation auf Dokument-Ebene, keine App-Klassen: jede Aenderung
  //    im Produktformular (Selling-Plan-Radios/-Selects werden dorthin
  //    injiziert) und die Mengen-Kacheln (liegen ausserhalb des Formulars).
  //    update() ist idempotent und liest den Zustand komplett frisch —
  //    grosszuegiges Triggern ist unkritisch.
  document.addEventListener('change', function (e) {
    const t = e.target;
    if (!t || !t.closest) return;
    if (
      t.name === 'selling_plan' ||
      t.closest('form[data-type="add-to-cart-form"]') ||
      t.closest('jk-quantity-breaks')
    ) {
      scheduleUpdate();
    }
  });

  // 2. Seals offizielle Events (verifiziert im Widget-Quelltext): Auswahl-
  //    Wechsel inkl. Plan-ID im detail, und Widget-fertig-gerendert.
  document.addEventListener('sealsubs:selling_plan_changed', function (e) {
    lastAnnouncedPlanId = String(e?.detail?.selling_plan_id ?? '');
    scheduleUpdate();
  });
  document.addEventListener('sealsubs:subscription_widget_created', scheduleUpdate);

  // 2b. Settle-Event aus jk-subscription-visibility.js (nach Seals asynchronem
  //     State-Update, 80ms nach Klick).
  document.addEventListener('jk:seal-option-change', scheduleUpdate);

  // 3. Fallback: MutationObserver auf dem Produktformular — faengt
  //    programmatische Re-Renders ab, die kein change-Event feuern.
  //    Der Preis-Block liegt ausserhalb des Formulars, keine Feedback-Schleife.
  let formObserver = null;
  function bindFormObserver() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    if (formObserver) formObserver.disconnect();
    formObserver = new MutationObserver(scheduleUpdate);
    // 'class' im Filter: Vue-Apps setzen Selektions-State oft nur als
    // Klassen-Toggle und .value-Property (kein Attribut, kein Event).
    formObserver.observe(form, {
      subtree:         true,
      childList:       true,
      attributes:      true,
      attributeFilter: ['checked', 'value', 'class']
    });
  }
  bindFormObserver();

  // 4. Variantenwechsel — Dawn publiziert variantChange NACH dem
  //    innerHTML-Swap des Preis-Blocks; update() liest danach frisch.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, scheduleUpdate);
  }

  // 5. Theme editor section reload
  document.addEventListener('shopify:section:load', function () {
    bindFormObserver();
    scheduleUpdate();
  });

  // Initial render (DOM already loaded since this runs deferred)
  update();
})();
